import { onTrack } from './lib/telemetry.ts';

/**
 * Google Analytics 4, loaded lazily and only when a measurement ID is baked in at build time
 * (VITE_GA_MEASUREMENT_ID, "G-XXXXXXX"). Sends the same named events as PostHog, plus GA's own
 * page_view. GA sets first-party _ga cookies; see docs/setup-guide.md Part 10 for the consent note.
 */
type Props = Record<string, string | number | boolean | undefined>;
declare global { interface Window { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void } }

const id = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const environment = (import.meta.env.VITE_ENVIRONMENT as string | undefined) || (import.meta.env.DEV ? 'local' : 'unknown');

export function mountGoogleAnalytics(): void {
  if (!id) return;
  const w = window;
  w.dataLayer = w.dataLayer ?? [];
  // The standard gtag stub: queues calls until gtag.js loads, and keeps working if it never does.
  w.gtag = w.gtag ?? function gtag() { w.dataLayer!.push(arguments); }; // eslint-disable-line prefer-rest-params
  w.gtag('js', new Date());
  w.gtag('config', id, { environment, send_page_view: true });
  onTrack((name, props) => {
    const clean: Props = { environment };
    for (const [k, v] of Object.entries(props ?? {})) if (v !== undefined) clean[k.replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 40)] = v;
    w.gtag!('event', name.replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 40), clean);
  });
  const load = () => {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
  };
  const ric = (w as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (ric) ric(load, { timeout: 3000 }); else w.setTimeout(load, 1500);
}
